import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { zodResolver } from "@hookform/resolvers/zod";
import { Immutable, castImmutable } from "immer";
import { atom, useAtomValue, useStore } from "jotai";
import { Trash } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import QRCode from "react-qr-code";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";
import { useMemoOne } from "use-memo-one";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { LayoutDetailQuery } from "@/gen/graphql";
import { CmsBlock, CmsLayout, zCmsLayout } from "@/gen/realtime";
import { cn } from "@/utils";

import { ContentEditor } from "./components/ContentEditor";
import { LayoutEditSidebar } from "./components/Sidebar";
import { useDragAndDrop } from "./dragAndDrop";
import { setupKeyboardHandlers } from "./keyboardHandlers";
import { initialState, updateState } from "./layoutEditState";
import { Action } from "./layoutEditState";
import { LayoutMetadata, zLayoutMetadataValues } from "./layoutMetadata";
import { UndoHandlerProvider, useUndoHandler } from "./undoHandler";

const layoutDetailQuery = graphql(`
    query LayoutDetail($layoutId: UUID!) {
        layout(id: $layoutId) {
            id
            content
            publishAt
            name
        }
    }
`);

const updateLayoutMutation = graphql(`
    mutation updateLayout($input: UpdateLayoutInput!) {
        updateLayout(input: $input) {
            id
        }
    }
`);

export default function LayoutDetailRoute() {
    const { id } = useParams();

    const [result] = useQuery({
        query: layoutDetailQuery,
        variables: {
            layoutId: id!,
        },
    });

    if (result.error) {
        return <div>{result.error.toString()}</div>;
    }

    if (!result.data) {
        return <div>Loading...</div>;
    }

    if (!result.data.layout) {
        return <div>Layout not found</div>;
    }

    return (
        <UndoHandlerProvider>
            <LayoutDetailView layout={result.data.layout} />
        </UndoHandlerProvider>
    );
}

function LayoutDetailView({ layout }: { layout: NonNullable<LayoutDetailQuery["layout"]> }) {
    const metadataForm = useForm<LayoutMetadata>({
        defaultValues: {
            publishAt: layout.publishAt,
            name: layout.name,
        },
        mode: "onSubmit",
        resolver: zodResolver(zLayoutMetadataValues),
    });

    const [_updateLayoutRes, updateLayout] = useMutation(updateLayoutMutation);

    const { onPatches, undo, flushChanges } = useUndoHandler();

    const store = useStore();
    const stateAtom = useMemoOne(() => atom(castImmutable(initialState())), []);
    const state = useAtomValue(stateAtom);

    // This is like a reducer, but we're not using a reducer since that will
    // call the mutateState function twice in Strict Mode. However since this
    // changes atoms it's not pure
    const dispatch = useCallback(
        (action: Action) => {
            const oldValue = store.get(stateAtom);
            const newValue = updateState(oldValue, action, store, onPatches);
            store.set(stateAtom, newValue);
            flushChanges();
        },
        [store, stateAtom, onPatches, flushChanges]
    );

    // Setup keyboard handlers (undo/redo, copy/paste)
    useEffect(() => {
        return setupKeyboardHandlers({
            store,
            stateAtom,
            dispatch,
            undo,
        });
    }, [dispatch, stateAtom, store, undo]);

    const { isDragging } = useDragAndDrop(dispatch);

    useEffect(() => {
        if (layout.content) {
            dispatch({
                type: "set-content",
                content: zCmsLayout.parse(layout.content),
            });
        }
    }, [layout.content, dispatch]);

    const save = () => {
        metadataForm.handleSubmit(async metadata => {
            const blocks: Record<string, Immutable<CmsBlock>> = {};

            for (const blockId of Object.keys(state.layout.blocks)) {
                blocks[blockId] = store.get(state.layout.blocks[blockId]!);
            }

            const content: Immutable<CmsLayout> = { ...state.layout, blocks };
            const result = await updateLayout({
                input: {
                    id: layout.id,
                    content,
                    ...metadata,
                },
            });

            if (result.error) {
                toast.error("Failed to save layout", {
                    description: result.error.message,
                });
            } else {
                toast.success("Layout saved successfully");
            }
        })();
    };

    if (!state) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <div className="mx-4 flex items-center gap-3">
                <Button onClick={save}>Save</Button>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button variant="secondary">Preview</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogTitle>Preview Layout</DialogTitle>
                        <DialogHeader>
                            <DialogDescription className="flex flex-col items-center">
                                <QRCode
                                    size={400}
                                    value={`taketaketake://preview-layout/${layout.id}`}
                                />
                                {/* <QRCode
                                    size={400}
                                    value={`https://taketaketake.com/preview-layout/${id}`}
                                /> */}

                                <div className="flex flex-col gap-2">
                                    <a href={`taketaketake://preview-layout/${layout.id}`}>
                                        taketaketake://preview-layout/{layout.id}
                                    </a>
                                    <Button variant="secondary" asChild>
                                        <a
                                            href={`https://taketaketake.com/preview-layout/${layout.id}`}
                                        >
                                            Preview on web
                                        </a>
                                    </Button>
                                </div>
                            </DialogDescription>
                        </DialogHeader>
                    </DialogContent>
                </Dialog>
                {isDragging && <DragToDelete />}
                <Button
                    onClick={() => {
                        dispatch({
                            type: "set-active-block",
                            blockId: null,
                        });
                    }}
                    className="ml-auto"
                >
                    Metadata
                </Button>
            </div>
            <div className="m-2 flex flex-row gap-4">
                <ContentEditor
                    dispatch={dispatch}
                    state={state}
                    blocks={state.layout.order}
                    depth={0}
                    parentId={null}
                    allParents={[]}
                />
                <LayoutEditSidebar
                    state={state}
                    dispatch={dispatch}
                    metadataForm={metadataForm}
                    onSubmit={save}
                />
            </div>
        </div>
    );
}

const DragToDelete = () => {
    const element = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    useEffect(() => {
        return dropTargetForElements({
            element: element.current!,
            getData() {
                return { t: "delete" };
            },
            onDragEnter: () => {
                setActive(true);
            },
            onDragLeave() {
                setActive(false);
            },
            onDrop() {
                setActive(false);
            },
        });
    });

    return (
        <div
            ref={element}
            className={cn(
                "mx-4 flex items-center gap-3 rounded bg-red-300 p-2",
                active ? "bg-red-600" : "bg-red-300"
            )}
        >
            <Trash /> Drag here to delete
        </div>
    );
};
